import { LoaderFunctionArgs, redirect } from 'react-router-dom'
import { toast } from 'react-toastify'

import { eventsApi } from '../../services/events'
import { AbstractSchema, AbstractReviewsSchema } from './Abstracts.schema'
import i18n from '../../i18n'
import type { AppStore } from '../../store'

export const submitAbstract =
  (store: AppStore) =>
  async ({ params, request }: LoaderFunctionArgs) => {
    const { eventId, abstractId } = params
    const shouldSubmit = new URL(request.url).searchParams.get('shouldSubmit') === 'true'
    const data = (await request.json()) as AbstractSchema
    const { t } = i18n

    const figures = [...data.figures]
    // @ts-expect-error figures should not be sent
    delete data.figures

    const result =
      abstractId === 'new'
        ? await store.dispatch(
            eventsApi.endpoints.addAbstract.initiate({
              eventId: Number(eventId),
              data,
            })
          )
        : await store.dispatch(
            eventsApi.endpoints.modifyAbstract.initiate({
              eventId: Number(eventId),
              id: Number(abstractId),
              data,
            })
          )

    if ('error' in result && result.error) {
      toast.error(t('Erreur lors de la sauvegarde du résumé'))
      return result
    }

    if ('data' in result && result.data) {
      const res = await Promise.all(
        figures.map(async figure => {
          return await (figure.id === -1
            ? store.dispatch(
                eventsApi.endpoints.addAbstractFigure.initiate({
                  eventId: Number(eventId),
                  abstractId: result.data.id,
                  data: figure,
                })
              )
            : store.dispatch(
                eventsApi.endpoints.modifyAbstractFigure.initiate({
                  eventId: Number(eventId),
                  abstractId: result.data.id,
                  id: figure.id,
                  title: figure.title,
                })
              ))
        })
      )

      toast.success(t('Résumé sauvegardé avec succès'))

      if (res.some(r => r && 'error' in r && r.error)) {
        toast.info(
          t(
            "Une erreur est survenue lors de la sauvegarde d'une figure mais votre résumé a bien été sauvegardé"
          )
        )
      }

      if (shouldSubmit) {
        const submitResult = await store.dispatch(
          eventsApi.endpoints.submitAbstract.initiate({
            eventId: Number(eventId),
            id: result.data.id,
          })
        )

        if ('error' in submitResult && submitResult.error) {
          toast.error(t('Erreur lors de la soumission du résumé'))
          return submitResult
        }

        toast.success(t('Résumé soumis avec succès'))
      }

      return redirect(`/abstracts/list/${eventId}`)
    }

    toast.error(t('Erreur lors de la sauvegarde du résumé'))
    return new Response(null, {
      status: 400,
      statusText: 'Failed to save abstract',
    })
  }

export const rateAbstract =
  (store: AppStore) =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const { eventId, abstractId } = params
    const shouldUpdate = new URL(request.url).searchParams.get('shouldUpdate') === 'true'
    const data = (await request.json()) as AbstractReviewsSchema

    delete data.has_conflict_of_interest

    const result = shouldUpdate
      ? await store.dispatch(
          eventsApi.endpoints.modifyAbstractReview.initiate({
            eventId: Number(eventId),
            id: Number(abstractId),
            data,
          })
        )
      : await store.dispatch(
          eventsApi.endpoints.reviewAbstract.initiate({
            eventId: Number(eventId),
            id: Number(abstractId),
            data,
          })
        )

    if ('error' in result && result.error) {
      toast.error(i18n.t('Erreur lors de la sauvegarde du résumé'))
      return result
    }

    toast.success(i18n.t('Résumé évalué avec succès'))
    return redirect(`/abstracts/evaluation/${eventId}`)
  }
